/** @jsx jsx */
import React, { memo, useRef } from 'react';
import { jsx, css } from '@compiled/react';
import { Inline, xcss } from '@atlaskit/primitives';
import Avatar, {
	AVATAR_SIZES,
	Skeleton,
	type AppearanceType,
	type AvatarPropTypes,
	type SizeType,
} from '@atlaskit/avatar';
import { MediaImage } from '@atlaskit/media-image';
import type { MediaImageWithMediaClientConfigProps } from '@atlaskit/media-image/internal-types';
import { SimpleTag } from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';
import { ColoredTag } from '@atlassian/jira-option-color-picker/src/colored-tag/index.tsx';
import { CellHoverProvider } from '@atlassian/jira-hover-popover/src/controllers/index.tsx';
import {
	CellHoverRenderer,
	HoverPopover,
	type CellHoverRendererProps,
} from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import Link from '../../common/ui/link/index.tsx';

export type TagItemMediaImageProps = Omit<MediaImageWithMediaClientConfigProps, 'children'>;

type TagItemIconProps = {
	url?: string;
	mediaImage?: TagItemMediaImageProps;
};

type Props = {
	/** The tag name to be displayed to users for the item */
	name: string;
	/** URL to direct users to when they click on the item */
	href?: string;
	/** Optional icon to display next to the item */
	icon?: TagItemIconProps;
	/** Optional boolean to apply strikethrough style to the item */
	shouldDisplayStrikethrough?: boolean;
	/** Optional boolean to check if colors should be added to the item */
	isColored?: boolean;
	/** Optional prop to enable hover popover */
	isHoverPopoverEnabled?: boolean;
	/** Optional prop to determine if the tag item is the only element present */
	isOnlyOneItem?: boolean;
};

const AVATAR_SIZE: SizeType = 'xsmall';
const AVATAR_APPEARANCE: AppearanceType = 'square';

const tagMarginFixStyles = css({
	display: 'inline-block',
	marginTop: token('space.negative.050'),
	marginBottom: token('space.negative.050'),
	maxWidth: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span': {
		maxWidth: '100%',
	},
});

const tagMarginInlineFixStyles = css({
	marginRight: token('space.negative.050'),
	marginLeft: token('space.negative.050'),
});

const strikethroughStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	a: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		textDecoration: 'line-through !important',
	},
});

const TagItemAvatar = ({
	src,
	testId = 'list-with-popup.ui.tag-item.avatar',
}: Pick<AvatarPropTypes, 'src' | 'testId'>) => (
	<Avatar
		appearance={AVATAR_APPEARANCE}
		size={AVATAR_SIZE}
		src={src}
		borderColor="transparent"
		testId={testId}
	/>
);

const TagItemMediaImage = memo(
	({ mediaImage, fallbackSrc }: { mediaImage: TagItemMediaImageProps; fallbackSrc?: string }) => (
		<MediaImage
			identifier={mediaImage.identifier}
			mediaClientConfig={mediaImage.mediaClientConfig}
			apiConfig={{
				...mediaImage.apiConfig,
				width: AVATAR_SIZES[AVATAR_SIZE],
				height: AVATAR_SIZES[AVATAR_SIZE],
			}}
		>
			{({ loading, error, data }) => {
				if (loading) {
					return <Skeleton appearance={AVATAR_APPEARANCE} size={AVATAR_SIZE} />;
				}

				if (error || !data?.src) {
					return (
						<TagItemAvatar
							src={fallbackSrc}
							testId="list-with-popup.ui.tag-item.media-image-error"
						/>
					);
				}

				return (
					<TagItemAvatar src={data?.src} testId="list-with-popup.ui.tag-item.media-image-avatar" />
				);
			}}
		</MediaImage>
	),
);

const TagItemIcon = ({ icon }: { icon: TagItemIconProps }) => (
	<>
		{icon.mediaImage ? (
			<TagItemMediaImage mediaImage={icon.mediaImage} fallbackSrc={icon.url} />
		) : (
			<TagItemAvatar src={icon.url} />
		)}
	</>
);

/**
 * An item formatted with an Atlaskit Tag wrapper.
 * It's recommended to use the TagItemPopupContentWrapper with this item component.
 */
export const TagItem = ({
	name,
	href,
	icon,
	shouldDisplayStrikethrough,
	isColored,
	isHoverPopoverEnabled = false,
	isOnlyOneItem,
}: Props) => {
	const tagRef = useRef(null);

	if (isHoverPopoverEnabled) {
		const renderTagContent = (
			ref: React.RefCallback<HTMLElement>,
			props?: CellHoverRendererProps,
		) => {
			return isColored ? (
				<ColoredTag
					labelName={name}
					href={href}
					actionSubjectId="listWithPopup"
					pressableXcss={extraColoredTagStyles}
					contentXcss={extraColoredTagTextStyles}
					analyticsAttributes={{
						isHoverPopoverEnabled,
					}}
					ref={ref}
					{...props}
				/>
			) : (
				<div css={hoverPopoverStyles}>
					<span
						data-testid="list-with-popup.ui.tag-item.tag-item"
						css={[
							tagMarginFixStyles,
							tagMarginInlineFixStyles,
							shouldDisplayStrikethrough && strikethroughStyles,
						]}
						{...props}
					>
						<SimpleTag
							elemBefore={icon ? <TagItemIcon icon={icon} /> : undefined}
							text={name}
							href={href}
							linkComponent={Link}
							ref={ref}
						/>
					</span>
				</div>
			);
		};

		const hoverPopoverContent = (props?: CellHoverRendererProps) => (
			<HoverPopover contentRef={tagRef} content={name}>
				{({ ref }) => renderTagContent(mergeRefs(tagRef, ref), props)}
			</HoverPopover>
		);

		if (isOnlyOneItem) {
			return hoverPopoverContent();
		}

		// If there are more then one tag, different popovers need to be rendered on the tag and the see more button.
		return (
			<CellHoverProvider>
				<CellHoverRenderer>
					{({ onMouseEnter, onMouseLeave }) => hoverPopoverContent({ onMouseEnter, onMouseLeave })}
				</CellHoverRenderer>
			</CellHoverProvider>
		);
	}

	if (isColored) {
		return (
			<ColoredTag
				labelName={name}
				href={href}
				actionSubjectId="listWithPopup"
				pressableXcss={extraColoredTagStyles}
				contentXcss={extraColoredTagTextStyles}
			/>
		);
	}

	return (
		<span
			data-testid="list-with-popup.ui.tag-item.tag-item"
			css={[
				tagMarginFixStyles,
				tagMarginInlineFixStyles,
				shouldDisplayStrikethrough && strikethroughStyles,
			]}
		>
			<SimpleTag
				elemBefore={icon ? <TagItemIcon icon={icon} /> : undefined}
				text={name}
				href={href}
				linkComponent={Link}
			/>
		</span>
	);
};

export const TagItemPopupContentWrapper = ({ children }: { children: React.ReactNode }) => (
	<Inline space="space.100" shouldWrap>
		{children}
	</Inline>
);

const extraColoredTagTextStyles = xcss({
	maxWidth: '100%',
});

const extraColoredTagStyles = xcss({
	marginInline: 'space.0',
	minWidth: '0px',
});

const hoverPopoverStyles = css({
	display: 'block',
	minWidth: token('space.0'),
});
